import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { Utilisateur } from 'src/app/utils/interfaces/new/utilisateur.interface';
import { OTPRequestObject, OTPVerifyObject, OTPResendObject, OTPRequestResponse } from 'src/app/utils/interfaces/new/otp.interface';
import { environment } from '../../../environments/environment';
import { Tools } from '../../utils/tools';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _wait = (ms) => new Promise(res => setTimeout(res, ms));

  constructor(
    private http: HttpClient,
    private _snack: SnackbarService,
    private _oauthService: OAuthService,
  ) {
    this.clearLegacyCookie('access-token');
  }

  /**
   * Rétrocompatibilité : supprime le cookie access-token s'il est encore accessible
   * en JavaScript (cookie non-HttpOnly émis par l'ancienne version du backend).
   * Sans cela, le navigateur enverrait un token expiré sur la requête de login,
   * ce qui bloquerait l'authentification côté serveur.
   */
  private clearLegacyCookie(name: string): void {
    const readable = document.cookie.split(';').some(c => c.trim().startsWith(`${name}=`));
    if (readable) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    }
  }

  public async login(email: string, password: string): Promise<Utilisateur | void> {
    this.clearLegacyCookie('access-token');
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const user = await this.http.post(`${environment.basePath}/auth/login`, {
        type: 'email',
        payload: {
          email,
          password
        }
      }, httpOptions).toPromise();
      const expiryWarning = user.headers.get('X-Password-Expiry-Warning');
      if (expiryWarning !== null) {
        const daysLeft = parseInt(expiryWarning, 10);
        if (daysLeft <= 0) {
          this._snack.openSnackBarWarning('Votre mot de passe semble ne pas avoir été modifié depuis un certain temps. Pour plus de sécurité, pensez à le mettre à jour.');
        } else {
          this._snack.openSnackBarWarning(`Votre mot de passe arrive à échéance dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}. Pensez à le mettre à jour pour plus de sécurité.`);
        }
      }
      return (user.body as Utilisateur);
    } catch (error) {
      throw error;
    }
  }

  public async loginWithCpx(id: string, message: string, signature: string, name: string): Promise<Utilisateur | void> {
    this.clearLegacyCookie('access-token');
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const payload = {
        id,
        message,
        signature,
        name
      };
      const user = await this.http.post(`${environment.basePath}/auth/login`, {
        type: 'cpx',
        payload
      }, httpOptions).toPromise();
      return (user.body as Utilisateur);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async loginWithPSC(code: string): Promise<{user: Utilisateur, message: string, authID: string}> {
    this.clearLegacyCookie('access-token');
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const payload = {
        code
      };
      const user = await this.http.post(`${environment.basePath}/auth/login`, {
        type: 'psc',
        payload
      }, httpOptions).toPromise();
      const message = user.headers.get('x-message');
      const authID = user.headers.get('X-AuthId');
      return {user: user.body as Utilisateur, message, authID};
    } catch (error) {
      throw (error.error.message);
    }
  }

  public async logout(authType: string, authId: string): Promise<void> {
    if (!authType || !authId) {
      // No more action to do
      return;
    }
    const httpOptions = {
      withCredentials: true,
      observe: 'response' as 'response'
    };
    switch (authType) {
      case 'psc':
        const pscReturn = await this.http.post(`${environment.basePath}/auth/logout`,
        {
          type: 'psc',
          payload: {
            authId
          }
        }, httpOptions).toPromise();
        const infoToken = pscReturn.body as any;


        if (infoToken && infoToken.idToken) {
          // On le rajoute à la main dans le sessionStorage
          sessionStorage.setItem('id_token', infoToken.idToken)
          // Redirection vers le logout PSC
          this._oauthService.logOut();
        }

        break;

      default:
        console.warn('Auth type not supported for logout: ' + authType)
        break;
    }
  }

  public async selectProfil(idProfil: string): Promise<Profil> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const profil = await this.http.post(`${environment.basePath}/auth/select-profil`, { id: idProfil }, httpOptions).toPromise();
      // Fix timeout pour Set-Cookie (sinon l'autre XHR est enchainé trop rapidement)
      await Tools.timeout(1000);
      return (profil.body as Profil);
    } catch (error) {
      throw error;
    }
  }

  public async selectDelegation(idDelegation: string): Promise<Profil> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const profil = await this.http.post(`${environment.basePath}/auth/delegation/select`, { id: idDelegation }, httpOptions).toPromise();
      // Fix timeout pour Set-Cookie (sinon l'autre XHR est enchainé trop rapidement)
      await Tools.timeout(1000);
      return (profil.body as Profil);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async quitDelegation(): Promise<Profil> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const profil = await this.http.post(`${environment.basePath}/auth/delegation/quit`, undefined, httpOptions).toPromise();
      // Fix timeout pour Set-Cookie (sinon l'autre XHR est enchainé trop rapidement)
      await Tools.timeout(1000);
      return (profil.body as Profil);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async logoutSession(): Promise<void> {
    try {
      await this.http.post(`${environment.basePath}/auth/logout`, { type: 'email', payload: {} }, { withCredentials: true }).toPromise();
    } catch (e) {
      // fire-and-forget : la session est déjà invalide ou le serveur est injoignable
    }
  }

  public async renewPassword(email: string, oldPassword: string, newPassword: string): Promise<boolean> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const token = sessionStorage.getItem('renew_password_token');
      let url = `${environment.basePath}/auth/renewPassword`;
      if (token) {
        url += `?token=${encodeURIComponent(token)}`;
      }
      const response = await this.http.post(url, {
        email,
        currentPassword: oldPassword,
        newPassword,
      }, httpOptions).toPromise();
      if (token) {
        sessionStorage.removeItem('renew_password_token');
      }
      return true;
    } catch (error) {
      throw error;
    }
  }

  public async forgotPassword(email: string): Promise<boolean> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response',
      };
      await this.http.post(`${environment.basePath}/auth/reset-password?email=${encodeURIComponent(email)}`, null, httpOptions).toPromise();
      return true;
    } catch (error) {
      throw error;
    }
  }

  public async requestOtp(otpRequest: OTPRequestObject): Promise<OTPRequestResponse | Utilisateur> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const response = await this.http.post(`${environment.basePath}/auth/otp/request`, otpRequest, httpOptions).toPromise();
      return (response.body as OTPRequestResponse | Utilisateur);
    } catch (error) {
      if (error?.error?.errorCode !== 'MustChangePassword') {
        this._snack.openSnackBar(error.error.message);
      } else {
        const renewSession = error.headers?.get('x-renew-session');
        if (renewSession) {
          sessionStorage.setItem('renew_password_token', renewSession);
        }
      }
      throw error;
    }
  }

  public async verifyOtp(otpVerify: OTPVerifyObject): Promise<{user: Utilisateur, message: string, authID: string}> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const response = await this.http.post(`${environment.basePath}/auth/otp/verify`, otpVerify, httpOptions).toPromise();
      const message = response.headers.get('X-Message');
      const authID = response.headers.get('X-AuthID');
      return {user: response.body as Utilisateur, message, authID};
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }

  public async resendOtp(otpResend: OTPResendObject): Promise<OTPRequestResponse> {
    try {
      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const response = await this.http.post(`${environment.basePath}/auth/otp/resend`, otpResend, httpOptions).toPromise();
      return (response.body as OTPRequestResponse);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }
}
